import React, { useState, useEffect } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import TransactionCard from './TransactionCard';
import TransactionSearch from './TransactionSearch';
import { transactionsApi } from '../../api/transactions';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';
import LoadingPng from '../../assets/images/Loading.png';
import { Pagination } from 'flowbite-react';

export default function Transaction() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { tokens, user } = useAuth();
  const [gameSettings, setGameSettings] = useState([]);
  const [filters, setFilters] = useState({
    drawDate: '',
    gameId: '',
    drawTime: '',
    search: '',
    type: '',
    page: 1,
    limit: 10,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [ticketLoading, setTicketLoading] = useState(false);

  useEffect(() => {
    const fetchGameSettings = async () => {
      try {
        if (!tokens?.access?.token) return;
        const response = await gamesApi.getAllGames(tokens.access.token);
        setGameSettings(response.data);
      } catch (error) {
        console.error('Game settings fetch error:', error);
      }
    };

    fetchGameSettings();
  }, [tokens?.access?.token]);

  useEffect(() => {
    console.log("Filters updated in Transaction component:", filters);
    
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        if (!tokens?.access?.token) return;
        
        console.log("API call with filters:", {
          page: filters.page,
          limit: filters.limit,
          drawDate: filters.drawDate,
          gameId: filters.gameId,
          drawTime: filters.drawTime,
          search: filters.search,
          type: filters.type
        });
        
        const response = await transactionsApi.getTransactions(
          tokens.access.token,
          filters.page,
          filters.limit,
          filters.drawDate,
          filters.gameId,
          filters.drawTime,
          filters.search,
          filters.type === 'Own' ? '' : filters.type
        );

        if (response.status === 200) {
          console.log("API Response:", response.data);
          const formattedTransactions = response.data.rows.map((tx) => ({
            game: tx.game?.name || 'Unknown',
            drawTime: tx.drawTime ? dayjs(`1970-01-01T${tx.drawTime}`).format('h:mm A') : '',
            drawDate: tx.drawDate ? dayjs(tx.drawDate).format('MMM DD YYYY hh:mmA') : '',
            amount: tx.totalAmount ? parseFloat(tx.totalAmount).toFixed(2) : '0.00',
            user: {
              name: tx.User?.name || 'Unknown',
              role: 'User',
            },
            ticketId: tx.id,
            bets: tx.bets,
            gameImage: tx.game?.game_settings?.otherSettings?.image,
          }));
          
          setTransactions(formattedTransactions);
          setTotalPages(Math.ceil(response.data.count / filters.limit));
        } else {
          throw new Error('Failed to fetch transactions');
        }
      } catch (error) {
        console.error('Transaction fetch error:', error);
        setError(error.message);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    if (tokens?.access?.token) {
      fetchTransactions();
    }
  }, [tokens?.access?.token, filters]);

  useEffect(() => {
    if (loading || ticketLoading) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [loading, ticketLoading]);

  const handleFilterChange = (newFilters) => {
    console.log("Filter change received:", newFilters);
    setLoading(true);
    setFilters(newFilters);
  };

  const handlePageChange = (page) => {
    setLoading(true);
    setFilters((prev) => ({ ...prev, page }));
  };

  return (
    <div className='min-h-screen relative overflow-hidden'>
      <HeaderWithIcon title='Transactions' />
      {(loading || ticketLoading) && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-75'>
          <img
            src={LoadingPng}
            alt='Loading'
            className='w-16 h-16 animate-spin'
          />
        </div>
      )}
      <div className='px-5 pt-4'>
        <TransactionSearch
          gameSettings={gameSettings}
          onFilterChange={handleFilterChange}
          currentFilters={filters}
        />
        <div className='divide-y divide-gray-200'>
          {transactions.length > 0 ? (
            transactions.map((result, index) => (
              <TransactionCard
                key={index}
                {...result}
                onTicketLoading={setTicketLoading}
              />
            ))
          ) : (
            <div className="py-4 text-center text-gray-500">
              {loading ? 'Loading transactions...' : 'No transactions found'}
            </div>
          )}
        </div>
        {totalPages > 1 && (
          <div className='flex justify-center my-4'>
            <Pagination
              currentPage={filters.page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
